import React, { PureComponent } from 'react'
import {connect} from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { isTablet, isMobile } from 'react-device-detect'
import DicomPreviewer from './DicomPreviewer'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import Icon from '@mdi/react'
import { mdiHistory, mdiClipboardListOutline } from '@mdi/js'
import { t } from '../i18n'
import 'react-perfect-scrollbar/dist/css/styles.css'
import Hammer from 'hammerjs'
import {
    seriesStore,
    filesStore,
    explorer,
    explorerActivePatientIndex,
    explorerActiveStudyIndex,
    explorerActiveSeriesIndex,
  } from '../actions'

import {
    buildStudySeriesMaps,
    buildSeriesMapForStudy,
} from '../utils/comparePriorStudy'

import * as cornerstone from "cornerstone-core"
import { getExplorerThumbnailThrottleMs } from '../utils/mobileDownloadProfile'
import {
    resolveSeriesTotalImages,
    countDownloadedSeriesImages,
} from '../utils/seriesImageCounts'
import {
    getExplorerThumbnailMaxConcurrent,
    isHeavyCineThumbnailFile,
    isPendingExplorerImageId,
    resolveExplorerThumbnailPreviewUrl,
    buildExplorerNonImageSlots,
    findOrdinalPairedDisplayableIndex,
    resolveExplorerSeriesFiles,
    listVisibleExplorerThumbnailSeries,
} from '../utils/explorerThumbnailPolicy'
import {
    isNonImageFileItem,
    isPdfDocumentFileItem,
    explorerNonImageBadge,
    buildNonImageThumbnailDataUrl,
} from '../utils/isNonImageInstance'
import { isRefFileItem } from '../utils/isRefSeries'
import ExplorerSeriesContextMenu from './ExplorerSeriesContextMenu'
import { attachBoostedWheelScroll } from '../utils/panelWheelScroll'

function studyUidFromFiles(files) {
    if (!Array.isArray(files) || files.length === 0) return ''
    for (let i = 0; i < files.length; i++) {
        const f = files[i]
        if (!f) continue
        const st = f.study || {}
        const uid = String(
            st.studyId || st.studyInstanceUID || f.studyInstanceUID || ''
        ).trim()
        if (uid) return uid
    }
    return ''
}

function studyUidOfFile(file) {
    if (!file) return ''
    const st = file.study || {}
    return String(
        st.studyId || st.studyInstanceUID || file.studyInstanceUID || ''
    ).trim()
}

/** Cuántos StudyInstanceUID distintos hay en el store (merge priors). */
function countDistinctStudyUids(files) {
    if (!Array.isArray(files) || !files.length) return 0
    const set = new Set()
    for (let i = 0; i < files.length; i++) {
        const u = studyUidOfFile(files[i])
        if (u) set.add(u)
    }
    return set.size
}

function filesStillHaveStudyUid(files, uid) {
    if (!uid || !Array.isArray(files)) return false
    const want = String(uid)
    for (let i = 0; i < files.length; i++) {
        const u = studyUidOfFile(files[i])
        if (u && u === want) return true
    }
    return false
}

function patientNameFromFiles(files) {
    if (!Array.isArray(files) || !files[0] || !files[0].patient) return ''
    const name = files[0].patient.patientName || ''
    return name === 'Legacy WADO' ? '' : name
}

/** Explorer puede montarse antes de handleExplorerUpdate (p.ej. abrir estudio local). */
function explorerPatientKeys(explorer) {
    if (
        explorer &&
        explorer.patient &&
        Array.isArray(explorer.patient.keys)
    ) {
        return explorer.patient.keys
    }
    return []
}

function explorerPatientName(explorer, index) {
    const keys = explorerPatientKeys(explorer)
    if (!keys.length) return ''
    const i = typeof index === 'number' && index >= 0 ? index : 0
    return keys[i] != null ? keys[i] : keys[0] || ''
}

const styles = theme => ({
    formControl: {
        margin: theme.spacing(1),
        minWidth: 180,
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            margin: theme.spacing(0.5),
        },
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    selectText: {
        fontSize: '0.85em',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.75em',
        },
    },    
    menuItemText: {
        fontSize: '0.85em',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.75em',
        },
    },
    explorerContainer: {
        width: '200px',
        padding: '8px 8px 8px 8px',
        marginTop: '40px',
        [theme.breakpoints.down('md')]: {
            width: '100%',
            padding: '4px',
            marginTop: 0,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: '#181818',
            borderTop: '1px solid #333',
            zIndex: 1000
        },
    },
    explorerStack: {
        width: '100%',
        marginTop: '10px',
        marginLeft: '0',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'row',
            gap: '4px',
            marginTop: 0,
            background: '#181818',
            padding: '4px',
            boxSizing: 'border-box',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            alignItems: 'stretch',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        },
    },
    explorerViewer: {
        position: 'relative',
        padding: 0,
        borderRadius: '4px',
        background: '#232323',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        border: '1.5px solid #444',
        minHeight: 220,
        height: 220,
        maxHeight: 220,
        minWidth: 180,
        maxWidth: 180,
        width: 180,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            borderColor: '#2196f3',
        },
        [theme.breakpoints.down('md')]: {
            minHeight: 160,
            height: 160,
            maxHeight: 160,
            minWidth: 130,
            maxWidth: 130,
            width: 130,
            borderRadius: '4px',
        },
    },
    miniOverlayTopLeft: {
        position: 'absolute',
        top: 4,
        left: 8,
        color: '#fff',
        fontSize: '0.95em',
        fontWeight: 600,
        textShadow: '1px 1px 2px #000',
        zIndex: 2,
    },
    miniOverlayBottomLeft: {
        position: 'absolute',
        bottom: 4,
        left: 8,
        color: '#fff',
        fontSize: '0.90em',
        fontWeight: 500,
        textShadow: '1px 1px 2px #000',
        zIndex: 2,
    },
    miniOverlayBottomRight: {
        position: 'absolute',
        bottom: 4,
        right: 8,
        color: '#fff',
        fontSize: '0.90em',
        fontWeight: 500,
        textShadow: '1px 1px 2px #000',
        zIndex: 2,
    },
})

// Función de utilidad para detectar el modo
const detectDeviceMode = () => {
    // Simplificar la detección para tratar tablets como móviles
    return isMobile || isTablet || window.innerWidth < 1024
}

class Explorer extends PureComponent {
    constructor(props) {
        super(props)
        this.dicomViewersRefs = []
        this.dicomViewers = []
        this.miniaturasRef = React.createRef()
        this.listRef = React.createRef()
        this.hammerManager = null

        // Determinar el modo inicial basado en el ancho y tipo de dispositivo
        const initialMode = detectDeviceMode()
        const isTabletDevice = isTablet || (window.innerWidth >= 768 && window.innerWidth <= 1024)
        
        this.state = {
            patientName: explorerPatientName(this.props.explorer, 0),
            studies: [],
            studyLabels: {},
            study: '',
            series: [],
            seriesActiveIndex: 0,
            isMobileBar: initialMode,
            isTablet: isTabletDevice,
            isTabletLandscape: isTabletDevice && window.innerWidth > window.innerHeight,
            thumbnails: {},
            renderMode: initialMode ? 'mobile' : 'desktop',
            showFullExplorer: !initialMode && !isTabletDevice,
            seriesContextMenu: null,
        }

        this._ctxLongPressTimer = null
        this._ctxLongPressFired = false

        // Intervalo para actualizar contadores
        this.counterUpdateInterval = null
        this._boundStudyUid = ''
        this._userPickedSeries = false
        this._pinnedSeriesKey = null

        // Inicializar los viewers solo si estamos en modo desktop
        if (!initialMode && !isTabletDevice) {
            for(let i=0; i < 8; i++) {
                this.dicomViewers.push(this.setDcmViewer(i))
            }
        }
    }

    componentDidMount() {
        this._isMounted = true;
        
        // Forzar el modo correcto inmediatamente después del montaje
        const currentMode = detectDeviceMode()
        const isTabletDevice = isTablet || (window.innerWidth >= 768 && window.innerWidth <= 1024)
        const isTabletLandscape = isTabletDevice && window.innerWidth > window.innerHeight

        if (currentMode !== this.state.isMobileBar || 
            isTabletDevice !== this.state.isTablet ||
            isTabletLandscape !== this.state.isTabletLandscape) {
            this.setState({ 
                isMobileBar: currentMode,
                renderMode: currentMode ? 'mobile' : 'desktop',
                showFullExplorer: !currentMode && !isTabletDevice,
                isTablet: isTabletDevice,
                isTabletLandscape
            });
        }

        window.addEventListener('resize', this.handleResize)
        this.handleResize() // Forzar detección inicial

        const patientIndex = this.props.explorerActivePatientIndex
        const studyIndex = this.props.explorerActiveStudyIndex
        const patientName =
            explorerPatientName(this.props.explorer, patientIndex) ||
            patientNameFromFiles(this.props.files)

        const files = Array.isArray(this.props.files) ? this.props.files : []
        
        this.setState({patientName: patientName}, () => {
            if (!files.length) {
                this.filesListForPatient = []
                this.studyList = new Map()
                this.seriesList = new Map()
                this.series = { seriesList: this.seriesList, seriesKeys: [] }
                this.setState({ study: '', studies: [], studyLabels: {}, series: [] })
                return
            }
            if (countDistinctStudyUids(files) > 1) {
                this.filesListForPatient = files.filter(Boolean)
            } else {
                this.filesListForPatient = files.filter((a) => {
                    return a && a.patient && a.patient.patientName === patientName
                })
                if (!this.filesListForPatient.length) {
                    this.filesListForPatient = files.slice()
                }
            }

            const { studyList, studyKeys, studyLabels } = buildStudySeriesMaps(
                this.filesListForPatient
            )
            this.studyList = studyList
            this.study = {
              list: this.studyList,
              keys: studyKeys
            }

            const studyIdx =
                typeof studyIndex === 'number' &&
                studyIndex >= 0 &&
                studyIndex < studyKeys.length
                    ? studyIndex
                    : 0
            const activeStudyKey = studyKeys[studyIdx] || studyKeys[0]
            const { seriesList, seriesKeys } = buildSeriesMapForStudy(
                activeStudyKey ? this.studyList.get(activeStudyKey) || [] : []
            )
            this.seriesList = seriesList
            
            this.series = {
                seriesList: this.seriesList,
                seriesKeys: seriesKeys
            }
            this.files = this.series.seriesList.get(seriesKeys[0])
            this.props.setSeriesStore(this.series)

            // Solo actualizamos el estado sin llamar a previewStackClick
            this.setState({
                study: activeStudyKey || '', 
                studies: studyKeys,
                studyLabels,
                series: seriesKeys
            })
        })

        // Solo usar Hammer.js en desktop
        if (this.miniaturasRef.current && !this.state.isMobileBar) {
            this.setupHammerManager()
        }

        // Generar miniaturas solo una vez al montar
        setTimeout(() => this.generateAllThumbnails(), 100)
        this.syncExplorerStackWheel()
    }

    handleResize = () => {
        const isMobileMode = detectDeviceMode()
        const isTabletDevice = isTablet || (window.innerWidth >= 768 && window.innerWidth <= 1024)
        const isTabletLandscape = isTabletDevice && window.innerWidth > window.innerHeight
        
        if (isMobileMode !== this.state.isMobileBar || 
            isTabletDevice !== this.state.isTablet ||
            isTabletLandscape !== this.state.isTabletLandscape) {
            this.setState({
                renderMode: isMobileMode ? 'mobile' : 'desktop',
                isMobileBar: isMobileMode,
                isTablet: isTabletDevice,
                isTabletLandscape,
                showFullExplorer: !isMobileMode && !isTabletDevice
            })
        }
    }

    setupHammerManager = () => {
        if (this.hammerManager) {
            this.hammerManager.destroy()
        }

        this.hammerManager = new Hammer.Manager(this.miniaturasRef.current, {
            recognizers: [
                [Hammer.Pan, { 
                    direction: this.state.isMobileBar ? Hammer.DIRECTION_HORIZONTAL : Hammer.DIRECTION_VERTICAL,
                    threshold: 5,
                    pointers: 1
                }],
                [Hammer.Tap, { event: 'tap' }]
            ]
        })

        let startScroll = 0
        let lastDelta = 0
        let rafId = null
        let isScrolling = false

        this.hammerManager.on('panstart', (e) => {
            const container = this.miniaturasRef.current
            if (container) {
                startScroll = this.state.isMobileBar ? container.scrollLeft : container.scrollTop
                lastDelta = 0
                isScrolling = true
                if (rafId) {
                    cancelAnimationFrame(rafId)
                }
            }
        })

        this.hammerManager.on('panmove', (e) => {
            if (!isScrolling) return
            
            const container = this.miniaturasRef.current
            if (container) {
                const delta = this.state.isMobileBar ? e.deltaX : e.deltaY
                rafId = requestAnimationFrame(() => {
                    if (this.state.isMobileBar) {
                        container.scrollLeft = startScroll - delta
                    } else {
                        container.scrollTop = startScroll - delta
                    }
                })
                lastDelta = delta
            }
        })

        this.hammerManager.on('panend', () => {
            isScrolling = false
            if (rafId) {
                cancelAnimationFrame(rafId)
            }

            // Aplicar inercia al scroll
            if (Math.abs(lastDelta) > 5) {
                const container = this.miniaturasRef.current
                if (container) {
                    const velocity = lastDelta * 0.1
                    let currentScroll = this.state.isMobileBar ? container.scrollLeft : container.scrollTop
                    const animate = () => {
                        if (Math.abs(velocity) > 0.5) {
                            currentScroll -= velocity
                            if (this.state.isMobileBar) {
                                container.scrollLeft = currentScroll
                            } else {
                                container.scrollTop = currentScroll
                            }
                            rafId = requestAnimationFrame(animate)
                        }
                    }
                    rafId = requestAnimationFrame(animate)
                }
            }
            startScroll = 0
            lastDelta = 0
        })

        this.hammerManager.on('tap', (e) => {
            let el = e.target
            while (el && !el.dataset.index && el !== this.miniaturasRef.current) {
                el = el.parentNode
            }
            if (el && el.dataset.index) {
                const idx = parseInt(el.dataset.index, 10)
                if (!isNaN(idx)) {
                    this.previewStackClick(idx)
                }
            }
        })
    }

    // Método para actualizar contadores cuando se cargan nuevas imágenes
    updateImageCounters = () => {
        // Forzar re-render para actualizar contadores
        this.forceUpdate()
    }

    // Método para iniciar actualización periódica de contadores durante la carga
    startCounterUpdates = () => {
        if (this.counterUpdateInterval) {
            clearInterval(this.counterUpdateInterval)
        }
        
        this.counterUpdateInterval = setInterval(() => {
            // Solo actualizar si hay series con imágenes cargándose
            const hasLoadingImages = this.state.series.some(seriesKey => {
                const seriesFiles = this.seriesList.get(seriesKey) || []
                const totalImagesInSeries = resolveSeriesTotalImages(seriesFiles)
                const fullyLoadedImages = countDownloadedSeriesImages(
                    seriesFiles,
                    this.isSeriesFileDownloaded
                )
                return fullyLoadedImages < totalImagesInSeries
            })
            
            if (hasLoadingImages) {
                this.updateImageCounters()
            } else {
                // Si todas las imágenes están cargadas, detener las actualizaciones
                this.stopCounterUpdates()
            }
        }, 500) // Actualizar cada 500ms
    }

    // Método para detener las actualizaciones periódicas
    stopCounterUpdates = () => {
        if (this.counterUpdateInterval) {
            clearInterval(this.counterUpdateInterval)
            this.counterUpdateInterval = null
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Si cambia el modo móvil/desktop, volver a configurar Hammer.js
        if (this.miniaturasRef.current && prevState.isMobileBar !== this.state.isMobileBar) {
            if (this.hammerManager) {
                this.hammerManager.destroy()
                this.hammerManager = null
            }
            if (!this.state.isMobileBar) {
                this.setupHammerManager()
            }
        }
        if (
            prevState.showFullExplorer !== this.state.showFullExplorer ||
            prevState.isMobileBar !== this.state.isMobileBar
        ) {
            this.syncExplorerStackWheel()
        }
        // Verificar si los archivos han cambiado o si hay nuevas series
        const filesChanged = prevProps.files !== this.props.files
        const seriesChanged = prevState.series !== this.state.series
        const seriesListChanged = prevProps.series !== this.props.series
        const collapsedChanged =
            prevProps.collapsedStudyKeys !== this.props.collapsedStudyKeys

        // Si los archivos han cambiado, actualizar la lista de archivos del paciente
        if (filesChanged) {
            const files = Array.isArray(this.props.files) ? this.props.files : []
            // clearStore / cambio de modo deja files=null un instante; no crashear
            if (files.length === 0) {
                this.filesListForPatient = []
                this.studyList = new Map()
                this.seriesList = new Map()
                this.series = { seriesList: this.seriesList, seriesKeys: [] }
                // No borrar _boundStudyUid / _userPickedSeries aquí:
                // un flush vacío momentáneo (clearStore) no debe forzar serie 0 al volver.
                this.resetThumbnailCaches()
                try {
                    this.props.setSeriesStore(this.series)
                } catch (e) {
                    /* ignore */
                }
                this.setState({ study: '', studies: [], studyLabels: {}, series: [], thumbnails: {} })
            } else {
                let patientName = this.state.patientName
                const multiStudy = countDistinctStudyUids(files) > 1
                // Merge priors: no filtrar por patientName exacto (manifest prior
                // puede traer "Unknown Patient" u otro PN → ocultaba el 2.º estudio).
                if (multiStudy) {
                    this.filesListForPatient = files.filter(Boolean)
                    if (!patientName) {
                        patientName = patientNameFromFiles(this.filesListForPatient)
                    }
                } else {
                    const hasCurrentPatient = files.some(
                        (a) =>
                            a &&
                            a.patient &&
                            a.patient.patientName === patientName
                    )
                    if (!patientName || !hasCurrentPatient) {
                        patientName = patientNameFromFiles(files)
                    }
                    this.filesListForPatient = files.filter((a) => {
                        return (
                            a &&
                            a.patient &&
                            a.patient.patientName === patientName
                        )
                    })
                    if (this.filesListForPatient.length === 0) {
                        this.filesListForPatient = files.slice()
                    }
                }

                if (this.filesListForPatient.length === 0) {
                    this.studyList = new Map()
                    this.seriesList = new Map()
                    this.series = { seriesList: this.seriesList, seriesKeys: [] }
                    this.resetThumbnailCaches()
                    this.setState({ patientName, study: '', studies: [], studyLabels: {}, series: [], thumbnails: {} })
                } else {
                    // Keys por StudyInstanceUID / SeriesInstanceUID (no fusionar priors)
                    const { studyList, studyKeys, studyLabels } = buildStudySeriesMaps(
                        this.filesListForPatient
                    )
                    this.studyList = studyList
                    this.study = {
                        list: this.studyList,
                        keys: studyKeys,
                    }

                    const prevBoundUid = this._boundStudyUid || ''
                    const prevStudy = this.state.study
                    // Conservar study seleccionado si sigue en el pool (p.ej. compare merge)
                    let nextStudy =
                        prevStudy && studyKeys.includes(prevStudy)
                            ? prevStudy
                            : studyKeys[0] || ''

                    let studyFiles =
                        nextStudy && this.studyList.has(nextStudy)
                            ? this.studyList.get(nextStudy) || []
                            : studyKeys.length > 0
                              ? this.studyList.get(studyKeys[0]) || []
                              : []

                    // UID del study seleccionado (no el primero del pool mergeado)
                    const nextUid =
                        studyUidFromFiles(studyFiles) ||
                        studyUidFromFiles(this.filesListForPatient)
                    // Solo switch real si el UID anterior ya no está en el pool
                    const realStudySwitch = !!(
                        nextUid &&
                        prevBoundUid &&
                        nextUid !== prevBoundUid &&
                        !filesStillHaveStudyUid(this.filesListForPatient, prevBoundUid)
                    )
                    if (realStudySwitch) {
                        nextStudy = studyKeys[0] || ''
                        studyFiles =
                            nextStudy && this.studyList.has(nextStudy)
                                ? this.studyList.get(nextStudy) || []
                                : []
                    }
                    if (studyFiles.length === 0) {
                        this.seriesList = new Map()
                        this.series = { seriesList: this.seriesList, seriesKeys: [] }
                        this.resetThumbnailCaches()
                        this.setState({
                            patientName,
                            study: nextStudy,
                            studies: studyKeys,
                            studyLabels,
                            series: [],
                            thumbnails: {},
                        })
                    } else {
                        const { seriesList, seriesKeys } = buildSeriesMapForStudy(studyFiles)
                        this.seriesList = seriesList

                        this.series = {
                            seriesList: this.seriesList,
                            seriesKeys: seriesKeys
                        }
                        this.props.setSeriesStore(this.series)

                        const firstUidBind = !!(nextUid && !prevBoundUid)
                        if (nextUid) {
                            this._boundStudyUid = nextUid
                        }

                        if (realStudySwitch) {
                            this.resetThumbnailCaches()
                        }

                        this.setState({
                            patientName,
                            study: nextStudy,
                            studies: studyKeys,
                            studyLabels,
                            series: seriesKeys,
                            ...(realStudySwitch ? { thumbnails: {} } : {}),
                        }, () => {
                            if (realStudySwitch) {
                                try {
                                    if (this.props.setExplorerActivePatientIndex) {
                                        this.props.setExplorerActivePatientIndex(0)
                                    }
                                    if (this.props.setExplorerActiveStudyIndex) {
                                        this.props.setExplorerActiveStudyIndex(0)
                                    }
                                } catch (e) {
                                    /* ignore */
                                }
                                this._userPickedSeries = false
                                this._pinnedSeriesKey = null
                                this.previewStackClick(this.findFirstDisplayableSeriesIndex(seriesKeys), { auto: true })
                                this.forceThumbnailRefresh()
                                if (typeof this.props.onAutoShowExplorer === 'function') {
                                    this.props.onAutoShowExplorer()
                                }
                                return
                            }

                            // Remapear índice activo si las seriesKeys se reordenaron
                            // (mantener la serie que el usuario está viendo).
                            const pinned =
                                this._pinnedSeriesKey != null
                                    ? this._pinnedSeriesKey
                                    : null
                            if (pinned != null && seriesKeys.length) {
                                const wantIdx = seriesKeys.indexOf(pinned)
                                const curIdx = this.props.explorerActiveSeriesIndex
                                if (
                                    wantIdx >= 0 &&
                                    typeof this.props.setExplorerActiveSeriesIndex ===
                                        'function' &&
                                    wantIdx !== curIdx
                                ) {
                                    try {
                                        this.props.setExplorerActiveSeriesIndex(wantIdx)
                                    } catch (e) {
                                        /* ignore */
                                    }
                                }
                            }

                            // Primera vez que conocemos el UID: autoseleccionar solo si
                            // App lo pide y el usuario aún no eligió otra serie.
                            const allowAuto =
                                this.props.autoSelectFirstSeries !== false &&
                                !this._userPickedSeries &&
                                this._pinnedSeriesKey == null
                            const activeIdx = this.props.explorerActiveSeriesIndex
                            const alreadyOnSeries =
                                typeof activeIdx === 'number' &&
                                activeIdx >= 0 &&
                                seriesKeys[activeIdx] != null
                            if (firstUidBind && allowAuto && !alreadyOnSeries) {
                                this.previewStackClick(this.findFirstDisplayableSeriesIndex(seriesKeys), { auto: true })
                                this.forceThumbnailRefresh()
                                if (typeof this.props.onAutoShowExplorer === 'function') {
                                    this.props.onAutoShowExplorer()
                                }
                            }
                        })
                    }
                }
            }
        }

        // Miniaturas / previews tras files: siempre cola de thumbs (webp/img).
        // No abrir DicomPreviewer legacy (openimage de XA Part-10 congela la UI).
        if (
            (filesChanged ||
                seriesChanged ||
                seriesListChanged ||
                collapsedChanged) &&
            (this.seriesList || this.studyList)
        ) {
            this.scheduleThumbnailRefresh()
        }
    }

    resetThumbnailCaches = () => {
        this._thumbnailSourceInstance = {}
        this._thumbnailInFlight = {}
        this._thumbnailStudyUid = {}
        this._thumbnailDataUrls = {}
        this._thumbnailQueue = []
        this._thumbnailActive = 0
        this._thumbGeneration = (this._thumbGeneration || 0) + 1
        this._lastThumbRefreshAt = 0
        this._lastDesktopPreviewAt = 0
        if (this._thumbRefreshTimer) {
            clearTimeout(this._thumbRefreshTimer)
            this._thumbRefreshTimer = null
        }
        if (this._desktopPreviewTimer) {
            clearTimeout(this._desktopPreviewTimer)
            this._desktopPreviewTimer = null
        }
    }

    forceThumbnailRefresh = () => {
        if (!this._isMounted || (!this.seriesList && !this.studyList)) return
        this.generateAllThumbnails()
        this.updateImageCounters()
        this.startCounterUpdates()
    }

    /** Throttle leading+trailing: genera thumbs aunque files cambie en bucle durante la descarga. */
    scheduleThumbnailRefresh = () => {
        if (!this._isMounted) return
        const minMs = getExplorerThumbnailThrottleMs()
        const now = Date.now()
        const last = this._lastThumbRefreshAt || 0
        const elapsed = now - last

        const run = () => {
            if (!this._isMounted) return
            this._lastThumbRefreshAt = Date.now()
            this._thumbRefreshTimer = null
            this.generateAllThumbnails()
            this.updateImageCounters()
            this.startCounterUpdates()
        }

        if (elapsed >= minMs) {
            if (this._thumbRefreshTimer) {
                clearTimeout(this._thumbRefreshTimer)
                this._thumbRefreshTimer = null
            }
            run()
        } else if (!this._thumbRefreshTimer) {
            this._thumbRefreshTimer = setTimeout(run, Math.max(48, minMs - elapsed))
        }
    }

    scheduleDesktopPreviewRefresh = (seriesKeys) => {
        if (!this._isMounted || !this.dicomViewersRefs) return
        const minMs = getExplorerThumbnailThrottleMs()
        const now = Date.now()
        const last = this._lastDesktopPreviewAt || 0
        const elapsed = now - last

        const run = () => {
            if (!this._isMounted) return
            this._lastDesktopPreviewAt = Date.now()
            this._desktopPreviewTimer = null
            // Solo thumbs img/webp — NO DicomPreviewer.openimage (XA Part-10 congela).
            this.generateAllThumbnails()
            this.updateImageCounters()
            this.startCounterUpdates()
        }

        if (elapsed >= minMs) {
            if (this._desktopPreviewTimer) {
                clearTimeout(this._desktopPreviewTimer)
                this._desktopPreviewTimer = null
            }
            run()
        } else if (!this._desktopPreviewTimer) {
            this._desktopPreviewTimer = setTimeout(run, Math.max(48, minMs - elapsed))
        }
    }

    componentWillUnmount() {
        this._isMounted = false
        if (this._ctxLongPressTimer) {
            clearTimeout(this._ctxLongPressTimer)
            this._ctxLongPressTimer = null
        };
        window.removeEventListener('resize', this.handleResize)
        if (this._autoShowTimeout) clearTimeout(this._autoShowTimeout);
        if (this._thumbRefreshTimer) clearTimeout(this._thumbRefreshTimer)
        if (this._desktopPreviewTimer) clearTimeout(this._desktopPreviewTimer)
        if (this.hammerManager) {
            this.hammerManager.destroy()
        }
        // Limpiar intervalo de actualización de contadores
        this.stopCounterUpdates()
        this.detachExplorerStackWheel()
    }

    syncExplorerStackWheel = () => {
        const el = this.miniaturasRef.current
        const want = !!this.state.showFullExplorer && !this.state.isMobileBar
        if (!want || !el) {
            this.detachExplorerStackWheel()
            return
        }
        if (el === this._explorerWheelEl && this._detachExplorerWheel) return
        this.detachExplorerStackWheel()
        this._explorerWheelEl = el
        this._detachExplorerWheel = attachBoostedWheelScroll(el)
    }

    detachExplorerStackWheel = () => {
        if (typeof this._detachExplorerWheel === 'function') {
            this._detachExplorerWheel()
            this._detachExplorerWheel = null
        }
        this._explorerWheelEl = null
    }

    handlePatientChange = (event, value) => {
        this.patientName = event.target.value
        const patientIndex = value.key

        this.filesListForPatient = this.props.allFiles.filter((a) => {
            return a.patient.patientName === this.patientName}
        )

        const { studyList, studyKeys, studyLabels } = buildStudySeriesMaps(
            this.filesListForPatient
        )
        this.studyList = studyList
        this.study = {
          list: this.studyList,
          keys: studyKeys
        }

        const { seriesList, seriesKeys } = buildSeriesMapForStudy(
            studyKeys[0] ? this.studyList.get(studyKeys[0]) || [] : []
        )
        this.seriesList = seriesList

        this.series = {
            seriesList: this.seriesList,
            seriesKeys: seriesKeys
        }
        this.props.setSeriesStore(this.series)

        this.setState({
            patientName: this.patientName,
            study: this.study.keys[0],
            studies: studyKeys,
            studyLabels,
            series: seriesKeys,
        }, () => {
            this.props.setExplorerActivePatientIndex(patientIndex)
            // Eliminamos la llamada automática a previewStackClick
        })
    }

    handleStudyChange = (event, value) => {
        const studyIndex = value.key

        const { studyList, studyKeys, studyLabels } = buildStudySeriesMaps(
            this.filesListForPatient
        )
        this.studyList = studyList
        this.study = {
          list: this.studyList,
          keys: studyKeys
        }

        const activeKey = studyKeys[studyIndex]
        const { seriesList, seriesKeys } = buildSeriesMapForStudy(
            activeKey ? this.studyList.get(activeKey) || [] : []
        )
        this.seriesList = seriesList

        this.series = {
            seriesList: this.seriesList,
            seriesKeys: seriesKeys
        }
        this.props.setSeriesStore(this.series)

        const bound = studyUidFromFiles(
            activeKey ? this.studyList.get(activeKey) || [] : []
        )
        if (bound) this._boundStudyUid = bound

        this.setState({
            study: this.study.keys[studyIndex],
            studies: studyKeys,
            studyLabels,
            series: seriesKeys,
        })
        // Eliminamos la llamada automática a previewStackClick
    }

    /**
     * Índice de serie con Pixel Data pareada a una PR/SR (N no-imagen + N imagen).
     * @param {number} nonImageIndex
     * @returns {number|null}
     */
    findPairedDisplayableSeriesIndex = (nonImageIndex) => {
        const keys = this.state.series || []
        const slots = buildExplorerNonImageSlots(keys, (k) =>
            this.getFirstSeriesFile(k)
        )
        return findOrdinalPairedDisplayableIndex(slots, nonImageIndex)
    }

    /**
     * Fallback async: parsear GSPS y abrir la serie que contiene el SOP referenciado.
     * @param {number} prIndex
     * @param {object} prFile
     */
    tryOpenReferencedSeriesFromPr = async (prIndex, prFile) => {
        if (!prFile || typeof this.props.onSelectSeries !== 'function') return
        try {
            const { parseGspsFromFile } = await import('../utils/gspsIndex')
            const parsed = await parseGspsFromFile(prFile)
            const uids = (parsed && parsed.referencedSopUids) || []
            if (!uids.length) return
            const want = new Set(uids.map((u) => String(u)))
            const keys = this.state.series || []
            for (let i = 0; i < keys.length; i++) {
                if (i === prIndex) continue
                const files = this.getSeriesFilesSorted(keys[i])
                const first = files && files[0]
                if (!first || isNonImageFileItem(first)) continue
                const hit = files.some((f) => {
                    if (!f) return false
                    const sop =
                        f.sopInstanceUID ||
                        f.SOPInstanceUID ||
                        (f.series &&
                            (f.series.sopInstanceUID || f.series.SOPInstanceUID)) ||
                        ''
                    return sop && want.has(String(sop))
                })
                if (!hit) continue
                this._pinnedSeriesKey = keys[i]
                this.props.onSelectSeries(files, i)
                return
            }
        } catch (e) {
            /* ignore */
        }
    }

    previewStackClick = (index, opts = {}) => {
        // Índice de serie lo fija App.loadNewSeries (evita re-render Explorer
        // síncrono en el mismo toque antes del paint).
        if (!opts.auto && typeof index === 'number' && index >= 0) {
            this._userPickedSeries = true
        }
        const seriesKey =
            opts.seriesKey != null ? opts.seriesKey : this.state.series[index]
        if (seriesKey != null) {
            this._pinnedSeriesKey = seriesKey
        }
        const seriesData =
            opts.seriesFiles != null
                ? opts.seriesFiles
                : this.getSeriesFilesSorted(seriesKey)
        // PR/GSPS/SR: sin Pixel Data → abrir la serie imagen pareada / referenciada.
        // Encapsulated PDF: abrir en PdfViewport (no redirigir).
        if (seriesData[0] && isNonImageFileItem(seriesData[0])) {
            if (isPdfDocumentFileItem(seriesData[0])) {
                if (Array.isArray(seriesData) && seriesData.length > 0) {
                    if (this.props.onSelectSeries) {
                        this.props.onSelectSeries(seriesData, index)
                    }
                } else if (this.props.setExplorerActiveSeriesIndex) {
                    this.props.setExplorerActiveSeriesIndex(index)
                }
                return
            }
            const pairedIdx = this.findPairedDisplayableSeriesIndex(index)
            if (
                pairedIdx != null &&
                pairedIdx !== index &&
                typeof this.props.onSelectSeries === 'function'
            ) {
                const pairKey = this.state.series[pairedIdx]
                const pairedFiles = this.getSeriesFilesSorted(pairKey)
                if (pairedFiles && pairedFiles.length > 0) {
                    this._pinnedSeriesKey = pairKey
                    this.props.onSelectSeries(pairedFiles, pairedIdx)
                    return
                }
            }
            if (typeof this.props.setExplorerActiveSeriesIndex === 'function') {
                this.props.setExplorerActiveSeriesIndex(index)
            }
            // Sin pareo ordinal (conteos distintos): intentar SOP referenciado del GSPS
            this.tryOpenReferencedSeriesFromPr(index, seriesData[0])
            return
        }
        if (Array.isArray(seriesData) && seriesData.length > 0) {
            if (this.props.onSelectSeries) {
                this.props.onSelectSeries(seriesData, index)
            }
        } else if (this.props.setExplorerActiveSeriesIndex) {
            this.props.setExplorerActiveSeriesIndex(index)
        }
    }

    previewStackTouch = (index, event) => {
        event.preventDefault()
        if (!event.target.closest('.ps__rail-y')) {
            this.previewStackClick(index)
        }
    }

    clear = () => {
        for(let i=0; i < 16; i++) {
            this.dicomViewersRefs[i].runTool('clear')
        }
    }

    onLoadedImage = (index) => {
        // Ya no necesitamos marcar las miniaturas como cargadas
    }

    onRenderedImage = () => {

    }

    getDcmViewerRef = (index) => {
        return this.dicomViewersRefs[index]
    }

    getDcmViewer = (index) => {
        return this.dicomViewers[index]
    }

    buildPreviewStack = (rows) => {
        const { classes } = this.props
        const stack = []
        for(let i=0; i < rows; i++) {
            const seriesKey = this.state.series[i]
            const seriesFiles = this.getSeriesFilesSorted(seriesKey)
            stack.push(
                <div 
                    key={i}
                    data-index={i}
                    className={'udv-explorer-thumb ' + classes.explorerViewer}
                    onClick={() => this.previewStackClick(i)}
                    style={{ cursor: 'pointer', minWidth: this.state.isMobileBar ? 80 : undefined, maxWidth: this.state.isMobileBar ? 80 : undefined }}
                >
                    {this.setDcmViewer(i, seriesFiles)}
                </div>
            )
        }
        return stack
    }

    setDcmViewer = (index, files) => {
        const { classes } = this.props
        return (
          <div className={'udv-explorer-thumb ' + classes.explorerViewer} style={{minHeight: 80, height: 80, maxHeight: 80}}>  
            <DicomPreviewer 
                dcmRef={(ref) => {this.dicomViewersRefs[index] = ref}}
                index={index}
                runTool={ref => (this.runTool = ref)} 
                changeTool={ref => (this.changeTool = ref)}
                onLoadedImage={this.onLoadedImage}
                onRenderedImage={this.onRenderedImage}
                visible={true}
                isMobile={this.props.isMobile}
                files={files}
            />   
          </div> 
        )
    }

    // Ordena archivos de serie: primera imagen = InstanceNumber más bajo
    getSeriesFilesSorted = (seriesKeyOrFiles) => {
        const seriesFiles = Array.isArray(seriesKeyOrFiles)
            ? seriesKeyOrFiles
            : resolveExplorerSeriesFiles(
                  seriesKeyOrFiles,
                  this.seriesList,
                  this.studyList
              )
        if (!seriesFiles.length) return []
        return [...seriesFiles].sort((a, b) => {
            const ia = (a && a.instanceNumber != null) ? Number(a.instanceNumber) : Number.MAX_SAFE_INTEGER
            const ib = (b && b.instanceNumber != null) ? Number(b.instanceNumber) : Number.MAX_SAFE_INTEGER
            if (ia !== ib) return ia - ib
            // desempate: ImagePosition / slice si existiera en el item
            const sa = a && a.sliceLocation != null ? Number(a.sliceLocation) : 0
            const sb = b && b.sliceLocation != null ? Number(b.sliceLocation) : 0
            return sa - sb
        })
    }

    // Primera imagen utilizable: preferir ya decodificada; si no, previewUrl/webp; si no, imageId
    getFirstSeriesFile = (seriesKeyOrFiles) => {
        const sorted = this.getSeriesFilesSorted(seriesKeyOrFiles)
        return (
            sorted.find((f) => f && f.imageId && f.image && f.image.getPixelData) ||
            sorted.find((f) => f && f.imageId && f.image && f.image.data) ||
            sorted.find((f) => f && resolveExplorerThumbnailPreviewUrl(f)) ||
            sorted.find((f) => f && f.imageId) ||
            sorted.find(Boolean) ||
            null
        )
    }

    /**
     * Primera serie con Pixel Data (salta PR/GSPS/SR).
     */
    findFirstDisplayableSeriesIndex = (seriesKeys) => {
        const keys = Array.isArray(seriesKeys) ? seriesKeys : []
        for (let i = 0; i < keys.length; i++) {
            const files = this.getSeriesFilesSorted(keys[i])
            const first = files && files[0]
            if (first && !isNonImageFileItem(first)) return i
        }
        return 0
    }

    /**
     * Miniatura para PR/SR/KO: preview del XA pareado (N PR + N XA) o placeholder etiquetado.
     */
    resolveNonImageExplorerThumbSrc = (index, first) => {
        const badge = explorerNonImageBadge(first) || 'PR'
        const placeholder = buildNonImageThumbnailDataUrl(badge)
        const keys = this.state.series || []
        const slots = buildExplorerNonImageSlots(keys, (k) => this.getFirstSeriesFile(k))
        const pairedIdx = findOrdinalPairedDisplayableIndex(slots, index)
        if (pairedIdx == null) return placeholder
        const pairKey = keys[pairedIdx]
        if (pairKey == null) return placeholder
        const pairFirst = this.getFirstSeriesFile(pairKey)
        const preview = resolveExplorerThumbnailPreviewUrl(pairFirst)
        if (preview) return preview
        const cached = this.state.thumbnails[pairKey]
        if (
            cached &&
            String(cached).indexOf('svg') === -1 &&
            String(cached).indexOf('No Img') === -1
        ) {
            return cached
        }
        return placeholder
    }

    /**
     * Encola generación de thumb con concurrencia limitada (evita N decode XA en paralelo).
     */
    enqueueThumbnail = (seriesKey) => {
        if (seriesKey == null) return
        if (!this._thumbnailQueue) this._thumbnailQueue = []
        if (!this._thumbnailInFlight) this._thumbnailInFlight = {}
        if (this._thumbnailInFlight[seriesKey]) return
        if (this._thumbnailQueue.indexOf(seriesKey) !== -1) return
        this._thumbnailQueue.push(seriesKey)
        this.pumpThumbnailQueue()
    }

    pumpThumbnailQueue = () => {
        if (!this._thumbnailQueue) this._thumbnailQueue = []
        const max = getExplorerThumbnailMaxConcurrent()
        while ((this._thumbnailActive || 0) < max && this._thumbnailQueue.length) {
            const seriesKey = this._thumbnailQueue.shift()
            this._thumbnailActive = (this._thumbnailActive || 0) + 1
            Promise.resolve()
                .then(() => this.generateThumbnail(seriesKey))
                .catch(() => {})
                .then(() => {
                    this._thumbnailActive = Math.max(0, (this._thumbnailActive || 1) - 1)
                    this.pumpThumbnailQueue()
                })
        }
    }

    // Genera miniatura para una serie a partir de la PRIMERA imagen (InstanceNumber mínimo)
    generateThumbnail = async (seriesKey) => {
        if (!this._thumbnailSourceInstance) this._thumbnailSourceInstance = {}
        if (!this._thumbnailInFlight) this._thumbnailInFlight = {}
        if (this._thumbnailInFlight[seriesKey]) return

        const first = this.getFirstSeriesFile(seriesKey)
        if (!first) return
        // PR/GSPS/SR: sin Pixel Data → no Cornerstone / placeholder
        if (isNonImageFileItem(first)) return

        const previewSrc = resolveExplorerThumbnailPreviewUrl(first)
        if (!first.imageId && !previewSrc) return

        const firstInstance = first.instanceNumber != null
            ? Number(first.instanceNumber)
            : Number.MAX_SAFE_INTEGER
        const usedInstance = this._thumbnailSourceInstance[seriesKey]
        const seriesUid = studyUidFromFiles(this.getSeriesFilesSorted(seriesKey)) || this._boundStudyUid
        const gen = this._thumbGeneration || 0
        // Solo saltear si ya hay miniatura REAL de esa instancia (no placeholder)
        // y corresponde al mismo estudio (seriesNumber se reutiliza entre estudios).
        const existing = this.state.thumbnails[seriesKey]
        const isPlaceholder = !existing || String(existing).indexOf('No Img') !== -1 || String(existing).indexOf('svg') !== -1
        const sameStudy = !!(seriesUid && this._thumbnailStudyUid && this._thumbnailStudyUid[seriesKey] === seriesUid)
        if (
            existing &&
            !isPlaceholder &&
            sameStudy &&
            usedInstance != null &&
            usedInstance <= firstInstance
        ) {
            return
        }

        const imageId = first.imageId || ''
        this._thumbnailInFlight[seriesKey] = true
        try {
            let dataUrl = null

            // XA/cine: usar URL https directa como thumb (display no requiere CORS).
            // Evitar canvas+crossOrigin: si R2 no manda ACAO, falla y deja el viewport sin path.
            if (previewSrc) {
                dataUrl = previewSrc
            } else {
                // Reutilizar image del store si ya se descargó (R2); no reencolar WADO
                let image =
                    first.image && typeof first.image.getPixelData === 'function'
                        ? first.image
                        : null
                if (!image) {
                    // Stub / cine pesado sin preview: no decode DICOM propio.
                    if (
                        isPendingExplorerImageId(imageId) ||
                        isHeavyCineThumbnailFile(first) ||
                        isNonImageFileItem(first)
                    ) {
                        return
                    }
                    image = await cornerstone.loadAndCacheImage(imageId)
                }
                // Si mientras cargaba ya hay una instancia más baja, no fijar esta miniatura
                const stillFirst = this.getFirstSeriesFile(seriesKey)
                if (stillFirst && stillFirst.imageId && stillFirst.imageId !== imageId) {
                    return
                }
                if (!image || typeof image.getPixelData !== 'function') return

                const canvas = document.createElement('canvas')
                canvas.width = 256
                canvas.height = 256
                cornerstone.renderToCanvas(canvas, image)
                dataUrl = canvas.toDataURL('image/png')
            }

            if (!dataUrl) return
            const usedNow = this._thumbnailSourceInstance[seriesKey]
            if (usedNow != null && usedNow < firstInstance) {
                return
            }
            if (gen !== (this._thumbGeneration || 0)) return
            const uidNow = studyUidFromFiles(this.getSeriesFilesSorted(seriesKey)) || this._boundStudyUid
            this._thumbnailSourceInstance[seriesKey] = firstInstance
            if (!this._thumbnailStudyUid) this._thumbnailStudyUid = {}
            this._thumbnailStudyUid[seriesKey] = uidNow
            if (!this._thumbnailDataUrls) this._thumbnailDataUrls = {}
            this._thumbnailDataUrls[seriesKey] = dataUrl
            if (!this._isMounted) return
            this.setState({ thumbnails: { ...this._thumbnailDataUrls } })
        } catch (e) {
            // No fijar "No Img" permanente durante descarga (reintento en el próximo pase)
            // Solo dejar el placeholder SVG del render si aún no hay thumb.
        } finally {
            if (this._thumbnailInFlight) {
                delete this._thumbnailInFlight[seriesKey]
            }
        }
    }

    // Genera miniaturas para todas las series visibles (o regenera si aparece la 1ª real)
    generateAllThumbnails = () => {
        const entries = listVisibleExplorerThumbnailSeries({
            studyKeys:
                (this.study && this.study.keys) || this.state.studies || [],
            collapsedStudyKeys: this.props.collapsedStudyKeys,
            studyList: this.studyList,
            boundSeriesKeys:
                (this.series && this.series.seriesKeys) ||
                this.state.series ||
                [],
            boundSeriesList: this.seriesList,
        })
        if (!entries.length) return
        entries.forEach((entry) => {
            if (entry && entry.seriesKey != null) {
                this.enqueueThumbnail(entry.seriesKey)
            }
        })
    }

    // Obtiene la miniatura de una serie
    getSeriesPreviewImage = (seriesKey) => {
        return this.state.thumbnails[seriesKey] || 'data:image/svg+xml;utf8,<svg width="90" height="90" xmlns="http://www.w3.org/2000/svg"><rect width="90" height="90" fill="%23222"/></svg>'
    }

    /**
     * Frame ya descargado/decodificado (no stub de esqueleto del manifest).
     * Los skeletons traen imageId para scroll inmediato, pero no cuentan en la barra.
     */
    isSeriesFileDownloaded = (file) => {
        if (!file || file.error) return false
        // stub de skeleton: imageId presente pero aún sin pipeline de carga
        if (file.skeleton === true) return false
        // Tras carga real: skeleton false (aunque image se haya soltado del store por RAM)
        if (file.imageId) return true
        // legacy: solo `image` sin imageId explícito
        return !!(file.image)
    }

    clearSeriesContextLongPress = () => {
        if (this._ctxLongPressTimer) {
            clearTimeout(this._ctxLongPressTimer)
            this._ctxLongPressTimer = null
        }
    }

    handleSeriesContextMenu = (e, index, seriesKey) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault()
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
        this.setState({
            seriesContextMenu: {
                anchorEl: e.currentTarget,
                explorerIndex: index,
                seriesKey,
            },
        })
    }

    handleSeriesContextTouchStart = (e, index, seriesKey) => {
        this._ctxLongPressFired = false
        this.clearSeriesContextLongPress()
        const target = e.currentTarget
        this._ctxLongPressTimer = setTimeout(() => {
            this._ctxLongPressFired = true
            this.setState({
                seriesContextMenu: {
                    anchorEl: target,
                    explorerIndex: index,
                    seriesKey,
                },
            })
        }, 500)
    }

    handleSeriesContextTouchEnd = (e) => {
        this.clearSeriesContextLongPress()
        if (this._ctxLongPressFired) {
            if (e && typeof e.preventDefault === 'function') e.preventDefault()
            this._ctxLongPressFired = false
        }
    }

    closeSeriesContextMenu = () => {
        this.setState({ seriesContextMenu: null })
    }

    handleSeriesMenuAction = (action, ctx = {}) => {
        const menu = this.state.seriesContextMenu || {}
        const explorerIndex =
            typeof ctx.explorerIndex === 'number'
                ? ctx.explorerIndex
                : menu.explorerIndex
        const seriesKey =
            ctx.seriesKey != null ? ctx.seriesKey : menu.seriesKey
        const seriesFiles =
            seriesKey != null ? this.getSeriesFilesSorted(seriesKey) : []

        if (action === 'regenerateThumbnail' && seriesKey != null) {
            if (this._thumbnailSourceInstance) {
                delete this._thumbnailSourceInstance[seriesKey]
            }
            if (this._thumbnailInFlight) {
                delete this._thumbnailInFlight[seriesKey]
            }
            if (this._thumbnailDataUrls) {
                delete this._thumbnailDataUrls[seriesKey]
            }
            if (this._thumbnailQueue && this._thumbnailQueue.length) {
                this._thumbnailQueue = this._thumbnailQueue.filter((k) => k !== seriesKey)
            }
            this.setState((prev) => {
                const thumbnails = { ...(prev.thumbnails || {}) }
                delete thumbnails[seriesKey]
                return { thumbnails }
            }, () => {
                this.enqueueThumbnail(seriesKey)
            })
            return
        }

        if (typeof this.props.onExplorerSeriesAction === 'function') {
            this.props.onExplorerSeriesAction(action, {
                explorerIndex,
                seriesKey,
                seriesFiles,
            })
        }
    }

    isSeriesPinnedManual = (index) => {
        const list = this.props.manualPinnedSeriesIndices
        return Array.isArray(list) && list.includes(index)
    }

    isSeriesPinnedAuto = (index) => {
        const list = this.props.autoPinnedSeriesIndices
        return Array.isArray(list) && list.includes(index)
    }

    /**
     * StudyUIDs actualmente abiertos en viewport(s) del layout.
     * @returns {Set<string>}
     */
    getViewportStudyUidSet = () => {
        const raw = this.props.viewportStudyUids
        const set = new Set()
        if (Array.isArray(raw)) {
            for (let i = 0; i < raw.length; i++) {
                const u = raw[i] != null ? String(raw[i]).trim() : ''
                if (u) set.add(u)
            }
        }
        return set
    }

    /**
     * Una o varias secciones de study (colapsables si hay ≥2 estudios mergeados).
     */
    renderExplorerSeriesStack = () => {
        const studyKeys = Array.isArray(this.state.studies) ? this.state.studies : []
        const labels = this.state.studyLabels || {}
        const collapsed = Array.isArray(this.props.collapsedStudyKeys)
            ? this.props.collapsedStudyKeys
            : []
        const multi = studyKeys.length > 1
        const vpUids = this.getViewportStudyUidSet()
        const mobile = !!this.state.isMobileBar || this.state.renderMode === 'mobile'

        if (!multi) {
            return (this.state.series || []).map((seriesKey, i) =>
                this.renderThumbnail({ index: i })
            )
        }

        const out = []
        studyKeys.forEach((studyKey) => {
            const studyFiles =
                this.studyList && this.studyList.has(studyKey)
                    ? this.studyList.get(studyKey) || []
                    : []
            const { seriesList, seriesKeys } = buildSeriesMapForStudy(studyFiles)
            const isCollapsed = collapsed.indexOf(studyKey) >= 0
            const studyUid = studyUidFromFiles(studyFiles)
            const inViewport = !!(studyUid && vpUids.has(studyUid))
            out.push(
                <div
                    key={`sec-${studyKey}`}
                    className="udv-explorer-study-sec"
                    style={{
                        width: mobile ? 108 : '100%',
                        minWidth: mobile ? 108 : undefined,
                        marginBottom: mobile ? 0 : 6,
                        marginRight: mobile ? 6 : 0,
                        flex: '0 0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: mobile ? '100%' : undefined,
                    }}
                >
                    <button
                        type="button"
                        className="udv-explorer-study-sec-head"
                        onClick={() => {
                            if (typeof this.props.onToggleStudyCollapse === 'function') {
                                this.props.onToggleStudyCollapse(studyKey)
                            }
                        }}
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 6px',
                            marginBottom: 4,
                            border: inViewport
                                ? '1px solid rgba(100,180,255,0.55)'
                                : '1px solid rgba(180,190,210,0.28)',
                            borderRadius: 4,
                            background: inViewport
                                ? 'rgba(50,90,140,0.55)'
                                : 'rgba(40,48,62,0.7)',
                            color: '#d8dde6',
                            fontSize: 11,
                            fontWeight: 600,
                            textAlign: 'left',
                            cursor: 'pointer',
                            boxShadow: inViewport
                                ? 'inset 3px 0 0 #64b5f6'
                                : 'none',
                        }}
                        title={labels[studyKey] || studyKey}
                    >
                        <span aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {labels[studyKey] || studyKey}
                        </span>
                        {inViewport ? (
                            <span
                                style={{
                                    flex: '0 0 auto',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#90caf9',
                                    letterSpacing: 0.2,
                                }}
                            >
                                en vista
                            </span>
                        ) : null}
                    </button>
                    {!isCollapsed
                        ? seriesKeys.map((sk, i) =>
                              this.renderThumbnail({
                                  index: i,
                                  seriesKey: sk,
                                  seriesFiles: seriesList.get(sk) || [],
                              })
                          )
                        : null}
                </div>
            )
        })
        return out
    }

    /** Miniaturas: un study o stack multi-study (desktop + móvil). */
    renderSeriesThumbnails = () => {
        const studyKeys = Array.isArray(this.state.studies) ? this.state.studies : []
        if (studyKeys.length > 1) {
            return this.renderExplorerSeriesStack()
        }
        return (this.state.series || []).map((seriesKey, i) =>
            this.renderThumbnail({ index: i })
        )
    }

    renderThumbnail = ({ index, seriesKey: seriesKeyOverride, seriesFiles: seriesFilesOverride }) => {
        const { classes } = this.props
        const seriesKey =
            seriesKeyOverride != null
                ? seriesKeyOverride
                : this.state.series[index]
        const isActive = this.props.explorerActiveSeriesIndex === index
        const seriesFiles =
            seriesFilesOverride != null
                ? this.getSeriesFilesSorted(seriesFilesOverride)
                : this.getSeriesFilesSorted(seriesKey)
        
        // Obtener el nombre de la serie desde el primer archivo
        const seriesName = seriesFiles.length > 0 && seriesFiles[0].series?.seriesDescription ? 
            seriesFiles[0].series.seriesDescription : 
            (seriesFiles.length > 0 && seriesFiles[0].image && seriesFiles[0].image.data && typeof seriesFiles[0].image.data.string === 'function' ? 
                seriesFiles[0].image.data.string('x0008103e') : 
                `Serie ${seriesKey}`)
        
        // Obtener el total de imágenes de la serie (manifest/Orthanc) vs cargadas
        const totalImagesInSeries = resolveSeriesTotalImages(seriesFiles)
        const fullyLoadedImages = countDownloadedSeriesImages(
            seriesFiles,
            this.isSeriesFileDownloaded
        )
        
        // Calcular progreso basado en imágenes completamente cargadas
        const progressPercentage = totalImagesInSeries > 0 ? (fullyLoadedImages / totalImagesInSeries) * 100 : 0
        
        const isRef = isRefFileItem(seriesFiles[0])
        const isNonImage = !!(seriesFiles[0] && isNonImageFileItem(seriesFiles[0]))
        const nonImageBadge = isNonImage
            ? explorerNonImageBadge(seriesFiles[0]) || 'PR'
            : ''
        const isPinnedManual = this.isSeriesPinnedManual(index)
        const isPinnedAuto = this.isSeriesPinnedAuto(index)
        
        // Placeholder / webp directo (no hace falta canvas; CORS solo para toDataURL)
        // PR/SR: preview del XA pareado (N↔N) o SVG etiquetado — no rect vacío.
        const previewSrc = resolveExplorerThumbnailPreviewUrl(
            this.getFirstSeriesFile(seriesFiles) || seriesFiles[0]
        )
        const src = isNonImage
            ? this.resolveNonImageExplorerThumbSrc(index, seriesFiles[0])
            : (
                this.state.thumbnails[seriesKey] ||
                previewSrc ||
                'data:image/svg+xml;utf8,<svg width="180" height="220" xmlns="http://www.w3.org/2000/svg"><rect width="180" height="220" fill="%23222"/></svg>'
            )
        
        return (
            <div
                key={
                    seriesKeyOverride != null
                        ? `thumb-${seriesKeyOverride}`
                        : index
                }
                data-index={index}
                className={
                    'udv-explorer-thumb ' +
                    classes.explorerViewer +
                    (isActive ? ' explorer-active' : '') +
                    (isPinnedManual || isPinnedAuto ? ' explorer-pinned' : '') +
                    (isRef ? ' explorer-has-ref' : '') +
                    (isNonImage ? ' explorer-nonimage' : '')
                }
                onClick={() =>
                    this.previewStackClick(index, {
                        seriesKey,
                        seriesFiles,
                    })
                }
                onContextMenu={(e) => this.handleSeriesContextMenu(e, index, seriesKey)}
                onTouchStart={(e) => this.handleSeriesContextTouchStart(e, index, seriesKey)}
                onTouchEnd={this.handleSeriesContextTouchEnd}
                onTouchCancel={this.handleSeriesContextTouchEnd}
                    title={
                      isNonImage
                        ? isPdfDocumentFileItem(seriesFiles[0])
                          ? `${nonImageBadge}: informe PDF (viewport)`
                          : `${nonImageBadge}: presentación (sin Pixel Data)`
                        : undefined
                    }
            >
                <img
                    src={src}
                    alt={isNonImage ? nonImageBadge : 'preview'}
                    className={
                        'explorer-thumbnail-img' +
                        (isNonImage ? ' explorer-thumbnail-nonimage' : '')
                    }
                    loading="lazy"
                    decoding="async"
                />
                {isNonImage ? (
                    <span
                        className="explorer-pr-badge"
                        aria-label={`Serie ${nonImageBadge}`}
                    >
                        {nonImageBadge}
                    </span>
                ) : null}
                {isRef ? (
                    <span className="explorer-ref-badge" aria-label="Serie de referencia">Ref</span>
                ) : null}
                {(isPinnedManual || isPinnedAuto) ? (
                    <span
                        className={'explorer-pin-indicator' + (isPinnedManual ? ' explorer-pin-manual' : ' explorer-pin-auto')}
                        aria-hidden="true"
                        title={isPinnedManual ? 'Prioridad manual' : 'Prioridad .Ref'}
                    />
                ) : null}
                {/* Nombre de la serie */}
                <div className="series-name-overlay">
                    <span className="series-name-text">
                        {seriesName}
                    </span>
                </div>
                {/* Contador de imágenes / etiqueta no-imagen */}
                <div className="series-image-counter">
                    <div className="counter-text">
                        {isNonImage ? (
                            <span className="loaded-count">{nonImageBadge}</span>
                        ) : (
                            <span className="loaded-count">
                                {fullyLoadedImages}/{totalImagesInSeries}
                            </span>
                        )}
                    </div>
                    {!isNonImage && totalImagesInSeries > 0 && (
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ 
                                    width: `${progressPercentage}%` 
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // Variables para distinguir tap vs swipe
    handleTouchStart = (e) => {
        if (e.touches && e.touches.length === 1) {
            this.touchStartX = e.touches[0].clientX;
            this.touchStartY = e.touches[0].clientY;
            this.touchStartTime = Date.now();
        }
    }

    handleTouchEnd = (index, e) => {
        if (e.changedTouches && e.changedTouches.length === 1) {
            const dx = Math.abs(e.changedTouches[0].clientX - this.touchStartX);
            const dy = Math.abs(e.changedTouches[0].clientY - this.touchStartY);
            const dt = Date.now() - this.touchStartTime;
            // Considerar tap si el movimiento es pequeño y el tiempo corto
            if (dx < 10 && dy < 10 && dt < 300) {
                this.previewStackClick(index);
            }
        }
    }

    renderSeriesContextMenu = () => {
        const menu = this.state.seriesContextMenu
        if (!menu || !menu.anchorEl) return null
        const idx = menu.explorerIndex
        const seriesKey = menu.seriesKey
        const seriesFiles = seriesKey != null ? this.getSeriesFilesSorted(seriesKey) : []
        const isRef = seriesFiles.length ? isRefFileItem(seriesFiles[0]) : false
        return (
            <ExplorerSeriesContextMenu
                anchorEl={menu.anchorEl}
                open={true}
                onClose={this.closeSeriesContextMenu}
                explorerIndex={idx}
                isPinnedManual={this.isSeriesPinnedManual(idx)}
                isRefSeries={isRef}
                hideRetryDownload={!!this.props.isPortalProfile}
                isPreviewMode={!!this.props.isPreviewMode}
                onAction={this.handleSeriesMenuAction}
            />
        )
    }

    render() {
        const { classes } = this.props
        const { renderMode, isTablet, isTabletLandscape, showFullExplorer } = this.state

        // Si estamos en modo móvil o tablet, mostrar la barra de miniaturas
        if (renderMode === 'mobile' || isTablet) {
            // embedded=true: vive dentro del Drawer (p. ej. toolbarEdge); no fixed full-bleed
            const embedded = !!this.props.embedded
            return (
                <>
                <div 
                    className={`${classes.explorerContainer} mobile-mode tablet-mode ${isTabletLandscape ? 'tablet-landscape' : ''}${embedded ? ' explorer-embedded' : ''}`}
                    style={embedded ? {
                        position: 'relative',
                        bottom: 'auto',
                        left: 'auto',
                        right: 'auto',
                        top: 'auto',
                        width: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        margin: 0,
                        padding: 0,
                        background: '#181818',
                        borderTop: 'none',
                        zIndex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                    } : {
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        top: 'auto',
                        width: '100vw',
                        height: 'auto',
                        maxHeight: '220px',
                        margin: 0,
                        padding: '4px',
                        background: '#181818',
                        borderTop: '1px solid #333',
                        zIndex: 1000,
                        overflow: 'hidden',
                        transform: 'translateZ(0)',
                        willChange: 'transform',
                        transition: 'none',
                        display: 'block'
                    }}
                >
                    {this.props.showRelatedStudies || this.props.showWorklist ? (
                        <div className="udv-explorer-historial-chip-wrap">
                            {this.props.showRelatedStudies ? (
                            <button
                                type="button"
                                className="udv-explorer-historial-chip is-compact"
                                onClick={() => {
                                    if (typeof this.props.onRelatedStudies === 'function') {
                                        this.props.onRelatedStudies()
                                    }
                                }}
                                title={t('relatedStudies.menuTooltip')}
                                aria-label={t('relatedStudies.menu')}
                            >
                                <Icon path={mdiHistory} size={0.65} color="currentColor" />
                                <span>
                                    {t('relatedStudies.chip')}
                                    {this.props.relatedStudiesCount > 0
                                        ? ` (${this.props.relatedStudiesCount})`
                                        : ''}
                                </span>
                            </button>
                            ) : null}
                            {this.props.showWorklist ? (
                            <button
                                type="button"
                                className="udv-explorer-historial-chip is-compact"
                                onClick={() => {
                                    if (typeof this.props.onWorklist === 'function') {
                                        this.props.onWorklist()
                                    }
                                }}
                                title={t('worklist.menuTooltip')}
                                aria-label={t('worklist.menu')}
                            >
                                <Icon path={mdiClipboardListOutline} size={0.65} color="currentColor" />
                                <span>
                                    {t('worklist.chip')}
                                    {this.props.worklistCount > 0
                                        ? ` (${this.props.worklistCount})`
                                        : ''}
                                </span>
                            </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div
                        ref={this.miniaturasRef}
                        className={`${classes.explorerStack} mobile-stack tablet-stack ${isTabletLandscape ? 'tablet-landscape-stack' : ''}`}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            flex: embedded ? 1 : undefined,
                            minHeight: embedded ? 0 : undefined,
                            height: embedded
                                ? this.props.showRelatedStudies
                                    ? 'auto'
                                    : '100%'
                                : 'auto',
                            maxHeight: embedded ? '100%' : '220px',
                            padding: embedded ? '0 2px' : '4px',
                            gap: embedded ? '2px' : '4px',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                            touchAction: 'pan-x',
                            alignItems: embedded ? 'stretch' : 'center',
                            justifyContent: 'flex-start',
                            background: '#181818',
                            transform: 'translateZ(0)',
                            willChange: 'transform',
                            transition: 'none',
                            boxSizing: 'border-box',
                            flex: embedded ? 1 : undefined,
                        }}
                    >
                        {this.renderSeriesThumbnails()}
                    </div>
                </div>
                {this.renderSeriesContextMenu()}
                </>
            );
        }

        // Renderizado para modo desktop (solo si showFullExplorer es true)
        if (showFullExplorer) {
            return (
                <>
                <div 
                    className={`${classes.explorerContainer} desktop-mode`}
                    style={{
                        position: 'relative',
                        width: '200px',
                        height: '100vh',
                        marginTop: '40px',
                        padding: '8px',
                        background: 'transparent',
                        border: 'none',
                        zIndex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                        {(() => {
                            const patientKeys =
                                this.props.explorer &&
                                this.props.explorer.patient &&
                                Array.isArray(this.props.explorer.patient.keys)
                                    ? this.props.explorer.patient.keys
                                    : []
                            const patientValue =
                                this.state.patientName &&
                                patientKeys.includes(this.state.patientName)
                                    ? this.state.patientName
                                    : ''
                            const studyKeys = Array.isArray(this.state.studies)
                                ? this.state.studies
                                : []
                            const studyLabels = this.state.studyLabels || {}
                            const studyValue =
                                this.state.study != null &&
                                this.state.study !== '' &&
                                studyKeys.includes(this.state.study)
                                    ? this.state.study
                                    : ''
                            const vpUids = this.getViewportStudyUidSet()
                            return (
                                <>
                        <FormControl className={classes.formControl}>
                            <InputLabel id="patient-label">Patient</InputLabel>
                            <Select
                                className={classes.selectText}
                                labelId="patient-select-label"
                                id="patient-select"
                                value={patientValue}
                                onChange={this.handlePatientChange}
                                displayEmpty
                            >
                                {patientKeys.map((patient, index) => (
                                    <MenuItem className={classes.menuItemText} value={patient} key={index}>{patient}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl className={classes.formControl}>
                            <InputLabel id="study-label">Study</InputLabel>
                            <Select
                                className={classes.selectText}
                                labelId="study-select-label"
                                id="study-select"
                                value={studyValue}
                                onChange={this.handleStudyChange}
                                displayEmpty
                            >
                                {studyKeys.map((study, index) => {
                                    const studyFiles =
                                        this.studyList && this.studyList.has(study)
                                            ? this.studyList.get(study) || []
                                            : []
                                    const uid = studyUidFromFiles(studyFiles)
                                    const inVp = !!(uid && vpUids.has(uid))
                                    const base = studyLabels[study] || study
                                    return (
                                        <MenuItem
                                            className={classes.menuItemText}
                                            value={study}
                                            key={index}
                                            style={
                                                inVp
                                                    ? {
                                                          borderLeft:
                                                              '3px solid #64b5f6',
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {inVp ? `${base} · en vista` : base}
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </FormControl>
                        {this.props.showRelatedStudies ? (
                            <Tooltip title={t('relatedStudies.menuTooltip')}>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="outlined"
                                    className="udv-explorer-historial-chip"
                                    onClick={() => {
                                        if (typeof this.props.onRelatedStudies === 'function') {
                                            this.props.onRelatedStudies()
                                        }
                                    }}
                                    startIcon={
                                        <Icon path={mdiHistory} size={0.7} color="currentColor" />
                                    }
                                    aria-label={t('relatedStudies.menu')}
                                >
                                    {t('relatedStudies.chip')}
                                    {this.props.relatedStudiesCount > 0
                                        ? ` (${this.props.relatedStudiesCount})`
                                        : ''}
                                </Button>
                            </Tooltip>
                        ) : null}
                        {this.props.showWorklist ? (
                            <Tooltip title={t('worklist.menuTooltip')}>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="outlined"
                                    className="udv-explorer-historial-chip"
                                    onClick={() => {
                                        if (typeof this.props.onWorklist === 'function') {
                                            this.props.onWorklist()
                                        }
                                    }}
                                    startIcon={
                                        <Icon
                                            path={mdiClipboardListOutline}
                                            size={0.7}
                                            color="currentColor"
                                        />
                                    }
                                    aria-label={t('worklist.menu')}
                                >
                                    {t('worklist.chip')}
                                    {this.props.worklistCount > 0
                                        ? ` (${this.props.worklistCount})`
                                        : ''}
                                </Button>
                            </Tooltip>
                        ) : null}
                                </>
                            )
                        })()}
                    </div>
                    <div
                        ref={this.miniaturasRef}
                        className={`${classes.explorerStack} desktop-stack`}
                        style={{
                            flex: 1,
                            width: '200px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '4px',
                            paddingBottom: 'calc(220px + 16px)',
                            gap: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginBottom: '8px'
                        }}
                    >
                        {this.renderSeriesThumbnails()}
                        <div style={{ height: '220px', width: '100%' }} />
                    </div>
                </div>
                {this.renderSeriesContextMenu()}
                </>
            );
        }

        // Si no es modo desktop ni móvil/tablet, no renderizar nada
        return null;
    }
}

const mapStateToProps = (state) => {
    return {
      files: state.files,
      explorerActivePatientIndex: state.explorerActivePatientIndex,
      explorerActiveStudyIndex: state.explorerActiveStudyIndex,
      explorerActiveSeriesIndex: state.explorerActiveSeriesIndex,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        setFilesStore: (files) => dispatch(filesStore(files)),
        setExplorer: (data) => dispatch(explorer(data)),
        setExplorerActivePatientIndex: (index) => dispatch(explorerActivePatientIndex(index)),
        setExplorerActiveStudyIndex: (index) => dispatch(explorerActiveStudyIndex(index)),
        setExplorerActiveSeriesIndex: (index) => dispatch(explorerActiveSeriesIndex(index)),
        setSeriesStore: (series) => dispatch(seriesStore(series)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Explorer))

// Modificar los estilos globales
if (typeof window !== 'undefined') {
    let style = document.getElementById('udv-explorer-global-styles')
    if (!style) {
      style = document.createElement('style');
      style.id = 'udv-explorer-global-styles'
      document.head.appendChild(style);
    }
    style.innerHTML = `
      .explorer-thumbnail-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #181818;
        display: block;
        border-radius: 4px;
      }
      .udv-explorer-thumb {
        position: relative;
      }
      .udv-explorer-historial-chip {
        align-self: stretch;
        margin: 0 8px;
        min-height: 32px;
        padding: 2px 10px;
        text-transform: none;
        font-size: 0.78rem;
        font-weight: 600;
        letter-spacing: 0.01em;
        color: #d8dde6 !important;
        border-color: rgba(180, 190, 210, 0.35) !important;
        background: rgba(40, 48, 62, 0.65) !important;
      }
      .udv-explorer-historial-chip:hover {
        border-color: rgba(196, 165, 116, 0.55) !important;
        background: rgba(55, 62, 78, 0.85) !important;
      }
      .udv-explorer-historial-chip-wrap {
        flex: 0 0 auto;
        display: flex;
        padding: 4px 6px 2px;
        box-sizing: border-box;
      }
      button.udv-explorer-historial-chip.is-compact {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        min-height: 28px;
        padding: 2px 10px;
        border-radius: 4px;
        border: 1px solid rgba(180, 190, 210, 0.35);
        background: rgba(40, 48, 62, 0.75);
        color: #d8dde6;
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;
      }
      button.udv-explorer-historial-chip.is-compact:hover {
        border-color: rgba(196, 165, 116, 0.55);
        background: rgba(55, 62, 78, 0.9);
      }
      .udv-studio-shell .udv-explorer-historial-chip,
      .udv-ohif-shell .udv-explorer-historial-chip {
        margin: 0 4px 4px;
      }
      .explorer-ref-badge {
        position: absolute;
        top: 4px;
        left: 4px;
        right: auto;
        z-index: 5;
        padding: 1px 5px;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0.02em;
        color: #e8f4ff;
        background: rgba(20, 40, 60, 0.72);
        border: 1px solid rgba(120, 180, 220, 0.45);
        border-radius: 999px;
        pointer-events: none;
        text-transform: none;
      }
      .explorer-pr-badge {
        position: absolute;
        top: 4px;
        left: 4px;
        right: auto;
        z-index: 6;
        padding: 1px 6px;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0.04em;
        color: #f5efe0;
        background: rgba(60, 48, 20, 0.82);
        border: 1px solid rgba(210, 170, 80, 0.55);
        border-radius: 999px;
        pointer-events: none;
        text-transform: uppercase;
      }
      .explorer-nonimage .explorer-thumbnail-img {
        opacity: 0.88;
        filter: saturate(0.85);
      }
      .explorer-nonimage .explorer-pin-indicator {
        top: 22px;
        left: 4px;
      }
      .explorer-has-ref .explorer-pin-indicator {
        top: 22px;
        left: 4px;
      }
      .explorer-nonimage.explorer-has-ref .explorer-ref-badge {
        top: 22px;
      }
      .explorer-nonimage.explorer-has-ref .explorer-pin-indicator {
        top: 40px;
      }
      .explorer-pin-indicator {
        position: absolute;
        top: 4px;
        left: 4px;
        z-index: 4;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255, 210, 80, 0.95);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.45);
        pointer-events: none;
      }
      .explorer-pin-indicator.explorer-pin-auto {
        background: rgba(120, 180, 220, 0.95);
      }
      .udv-explorer-thumb.explorer-pinned:not(.explorer-active) {
        box-shadow: inset 0 0 0 1px rgba(255, 210, 80, 0.35);
      }
      .series-name-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.85);
        color: #fff;
        padding: 4px 6px;
        font-size: 0.75em;
        font-weight: 600;
        border-radius: 4px 4px 0 0;
        z-index: 3;
        backdrop-filter: blur(2px);
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .series-name-text {
        display: block;
        text-shadow: 1px 1px 2px #000;
        font-size: 0.9em;
        line-height: 1.2;
        color: #fff;
        font-weight: 600;
      }
      .series-image-counter {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.85);
        color: #fff;
        padding: 4px 6px;
        font-size: 0.75em;
        font-weight: 600;
        border-radius: 0 0 4px 4px;
        z-index: 3;
        backdrop-filter: blur(2px);
      }
      .counter-text {
        text-align: center;
        margin-bottom: 2px;
        text-shadow: 1px 1px 2px #000;
        font-size: 0.9em;
        line-height: 1.2;
      }
      .loaded-count {
        color: #4caf50;
        font-weight: 600;
        font-size: 1.2em;
      }
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.6; }
        100% { opacity: 1; }
      }
      .progress-bar {
        width: 100%;
        height: 3px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
        overflow: hidden;
        margin-top: 2px;
      }
      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #4caf50, #8bc34a);
        transition: width 0.3s ease;
        border-radius: 2px;
      }
      
      /* Estilos específicos para móvil */
      @media (max-width: 1024px), (pointer: coarse) {
        .series-name-overlay {
          padding: 3px 4px;
          font-size: 0.7em;
        }
        .series-name-text {
          font-size: 0.8em;
        }
        .series-image-counter {
          padding: 3px 4px;
          font-size: 0.75em;
        }
        .counter-text {
          font-size: 0.8em;
          margin-bottom: 1px;
        }
        .loaded-count {
          font-size: 1.0em;
        }
        .progress-bar {
          height: 3px;
          margin-top: 2px;
        }
      }
      
      /* Estilos específicos para tablet - mantener la barra de progreso visible */
      @media (pointer: coarse) and (min-width: 768px) and (max-width: 1024px) {
        .progress-bar {
          height: 4px !important;
          margin-top: 3px !important;
          background: rgba(255, 255, 255, 0.3) !important;
        }
        .progress-fill {
          background: linear-gradient(90deg, #4caf50, #8bc34a) !important;
          border-radius: 2px !important;
        }
        .series-image-counter {
          padding: 4px 6px !important;
          font-size: 0.8em !important;
        }
        .counter-text {
          font-size: 0.85em !important;
          margin-bottom: 2px !important;
        }
        .loaded-count {
          font-size: 1.1em !important;
          color: #4caf50 !important;
        }
      }
      .explorer-active {
        border: 2px solid #2196f3 !important;
      }
      .explorerStack {
        scrollbar-width: thin;
        scrollbar-color: #444 #181818;
      }
      .mobile-mode {
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        top: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        transform: translateZ(0) !important;
        will-change: transform !important;
        transition: none !important;
        display: block !important;
      }
      .desktop-mode {
        position: relative !important;
        transform: none !important;
        will-change: auto !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .mobile-stack {
        display: flex !important;
        flex-direction: row !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        touch-action: pan-x !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      .mobile-stack::-webkit-scrollbar {
        display: none !important;
      }
      .desktop-stack {
        display: flex !important;
        flex-direction: column !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }
      .tablet-mode {
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        top: auto !important;
        width: 100vw !important;
        max-width: 100vw !important;
        height: auto !important;
        max-height: 220px !important;
        transform: translateZ(0) !important;
        will-change: transform !important;
        transition: none !important;
        display: block !important;
        background: #181818 !important;
        border-top: 1px solid #333 !important;
        z-index: 1000 !important;
      }
      .tablet-stack {
        display: flex !important;
        flex-direction: row !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        touch-action: pan-x !important;
        width: 100% !important;
        max-width: 100% !important;
        background: #181818 !important;
      }
      .tablet-stack::-webkit-scrollbar {
        display: none !important;
      }
      @media (max-width: 1024px), (pointer: coarse) {
        .explorerContainer {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          top: auto !important;
          width: 100vw !important;
          max-width: 100vw !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
          transition: none !important;
          display: block !important;
        }
        .explorerStack {
          display: flex !important;
          flex-direction: row !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          touch-action: pan-x !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        .explorerStack::-webkit-scrollbar {
          display: none !important;
        }
      }
      /* Forzar estilos específicos para tablets */
      @media (pointer: coarse) and (min-width: 768px) and (max-width: 1024px) {
        /* Ocultar completamente el modo desktop en tablets */
        .desktop-mode {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          pointer-events: none !important;
          width: 0 !important;
          height: 0 !important;
          overflow: hidden !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: none !important;
        }

        /* Forzar el modo móvil en tablets */
        .explorerContainer {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          top: auto !important;
          width: 100vw !important;
          max-width: 100vw !important;
          height: auto !important;
          max-height: 220px !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
          transition: none !important;
          display: block !important;
          background: #181818 !important;
          border-top: 1px solid #333 !important;
          z-index: 1000 !important;
          margin: 0 !important;
          padding: 4px !important;
          box-sizing: border-box !important;
        }

        .explorerStack {
          display: flex !important;
          flex-direction: row !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          touch-action: pan-x !important;
          width: 100% !important;
          max-width: 100% !important;
          background: #181818 !important;
          margin: 0 !important;
          padding: 4px !important;
          box-sizing: border-box !important;
          gap: 4px !important;
        }

        .explorerStack::-webkit-scrollbar {
          display: none !important;
        }

        /* Estilos específicos para tablet en landscape */
        .tablet-landscape {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          top: auto !important;
          width: 100vw !important;
          max-width: 100vw !important;
          height: auto !important;
          max-height: 220px !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
          transition: none !important;
          display: block !important;
          background: #181818 !important;
          border-top: 1px solid #333 !important;
          z-index: 1000 !important;
          margin: 0 !important;
          padding: 4px !important;
          box-sizing: border-box !important;
        }
        
        /* Asegurar que la barra de progreso sea visible en tablet landscape */
        .tablet-landscape .progress-bar {
          height: 4px !important;
          margin-top: 3px !important;
          background: rgba(255, 255, 255, 0.3) !important;
        }
        .tablet-landscape .progress-fill {
          background: linear-gradient(90deg, #4caf50, #8bc34a) !important;
          border-radius: 2px !important;
        }
        .tablet-landscape .series-image-counter {
          padding: 4px 6px !important;
          font-size: 0.8em !important;
        }
        .tablet-landscape .counter-text {
          font-size: 0.85em !important;
          margin-bottom: 2px !important;
        }
        .tablet-landscape .loaded-count {
          font-size: 1.1em !important;
          color: #4caf50 !important;
        }

        .tablet-landscape-stack {
          display: flex !important;
          flex-direction: row !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          touch-action: pan-x !important;
          width: 100% !important;
          max-width: 100% !important;
          background: #181818 !important;
          margin: 0 !important;
          padding: 4px !important;
          box-sizing: border-box !important;
          gap: 4px !important;
        }

        .tablet-landscape-stack::-webkit-scrollbar {
          display: none !important;
        }

        /* Asegurar que no haya residuos de la barra vertical */
        .explorerContainer > *:not(.explorerStack) {
          display: none !important;
        }

        /* Asegurar que no haya residuos de la barra vertical en modo tablet */
        .tablet-mode > *:not(.tablet-stack) {
          display: none !important;
        }

        /* Asegurar que no haya residuos de la barra vertical en modo tablet landscape */
        .tablet-landscape > *:not(.tablet-landscape-stack) {
          display: none !important;
        }
      }

      /* toolbarEdge: Explorer embebido en panel con muesca (no fixed a viewport) */
      .explorer-embedded.mobile-mode,
      .explorer-embedded.tablet-mode,
      .explorer-embedded.tablet-landscape,
      .explorerContainer.explorer-embedded,
      .explorer-embedded {
        position: relative !important;
        bottom: auto !important;
        left: auto !important;
        right: auto !important;
        top: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        transform: none !important;
        will-change: auto !important;
        z-index: 1 !important;
        display: flex !important;
        flex-direction: column !important;
        border-top: none !important;
        background: #181818 !important;
      }
      .explorer-embedded .mobile-stack,
      .explorer-embedded .tablet-stack,
      .explorer-embedded .tablet-landscape-stack,
      .explorer-embedded .explorerStack {
        position: relative !important;
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        flex: 1 1 auto !important;
        padding: 0 2px !important;
        gap: 2px !important;
        align-items: stretch !important;
        box-sizing: border-box !important;
      }
      /* Miniaturas edge: clase estable udv-explorer-thumb (prod JSS no siempre incluye "explorerViewer") */
      .explorer-embedded .udv-explorer-thumb {
        height: 100% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        width: auto !important;
        min-width: 88px !important;
        max-width: 120px !important;
        margin: 0 !important;
        border-radius: 0 !important;
        border-left: none !important;
        border-right: 1px solid #333 !important;
        border-top: none !important;
        border-bottom: none !important;
        box-shadow: none !important;
        background: #111 !important;
        flex: 0 0 auto !important;
      }
      .explorer-embedded .udv-explorer-thumb.explorer-active {
        border: 2px solid #2196f3 !important;
        box-shadow: inset 0 0 0 1px rgba(33, 150, 243, 0.35) !important;
        z-index: 2 !important;
      }
      .explorer-embedded .explorer-thumbnail-img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
      }
    `;
}

